import {
  ADVERTISED_DISCOVER_BOARDS,
  type AdvertisedBoardRoute,
  type AdvertisedDiscoverBoard,
  ALL_STATES_BOARD_ROUTE,
  DEPRECATED_DISCOVER_BOARDS,
  type DeprecatedBoardRoute,
  type DeprecatedDiscoverBoard,
  type DiscoverBoardRoute,
  type DiscoverBoardState,
  isDeprecatedDiscoverBoard,
} from "@nimble/datamodel/enums/discoverBoards";
import {
  STATE_NAME,
  type StateAbbreviation,
} from "@nimble/datamodel/enums/states";
import { StaticImageData } from "next/image";
import { z } from "zod";

import arkansasLogoPng from "@/lib/assets/arkansas-logo.png";
import indianaLogoPng from "@/lib/assets/indiana-logo.png";
import missouriLogoPng from "@/lib/assets/missouri-logo.png";
import oklahomaLogoPng from "@/lib/assets/oklahoma-logo.png";
import oklahomaMobileLogoPng from "@/lib/assets/oklahoma-mobile-logo.png";
import { type Email, email } from "@/lib/schemas/email";

const attributionNameSchema = z.string().brand<"AttributionName">();
export type AttributionName = z.infer<typeof attributionNameSchema>;

const attributionUrlSchema = z.string().url().brand<"AttributionUrl">();
export type AttributionUrl = z.infer<typeof attributionUrlSchema>;

export type SalaryScheduleAttribution = {
  name: AttributionName;
  url: AttributionUrl;
};

export const salaryScheduleAttribution = (
  name: string,
  url: string,
): SalaryScheduleAttribution => ({
  name: attributionNameSchema.parse(name),
  url: attributionUrlSchema.parse(url),
});

const boardTitleSchema = z.string().brand<"BoardTitle">();
export type BoardTitle = z.infer<typeof boardTitleSchema>;

const searchAddressSchema = z.string().brand<"SearchAddress">();
export type SearchAddress = z.infer<typeof searchAddressSchema>;

const topCitySchema = z.string().brand<"TopCity">();
export type TopCity = z.infer<typeof topCitySchema>;

const searchInLabelSchema = z.string().brand<"SearchInLabel">();
export type SearchInLabel = z.infer<typeof searchInLabelSchema>;

const doeNameSchema = z.string().brand<"EducationOrganizationName">();
export type EducationOrganizationName = z.infer<typeof doeNameSchema>;

const doeAbbreviationSchema = z
  .string()
  .brand<"EducationOrganizationAbbreviation">();
export type EducationOrganizationAbbreviation = z.infer<
  typeof doeAbbreviationSchema
>;

const title = (value: string): BoardTitle => boardTitleSchema.parse(value);
const address = (value: string): SearchAddress =>
  searchAddressSchema.parse(value);
const cities = (...values: string[]): readonly TopCity[] =>
  values.map((v) => topCitySchema.parse(v));
const searchIn = (value: string): SearchInLabel =>
  searchInLabelSchema.parse(value);
const doeName = (value: string): EducationOrganizationName =>
  doeNameSchema.parse(value);
const doeAbbr = (value: string): EducationOrganizationAbbreviation =>
  doeAbbreviationSchema.parse(value);

interface BoardPresentation {
  readonly title: BoardTitle;
  readonly defaultSearchAddress: SearchAddress;
  readonly topCities: readonly TopCity[];
  readonly searchInLabel: SearchInLabel | null;
  readonly educationOrganizationName: EducationOrganizationName;
  readonly educationOrganizationAbbreviation: EducationOrganizationAbbreviation;
  readonly logo: StaticImageData | null;
  readonly mobileLogo: StaticImageData | null;
  readonly salaryAttribution: SalaryScheduleAttribution | null;
}

/** Boards we advertise. A deprecated board declares itself in DEPRECATED_BOARDS. */
const DISCOVER_BOARD_PRESENTATION = {
  AR: {
    title: title(STATE_NAME.ARKANSAS),
    defaultSearchAddress: address("Arkansas"),
    topCities: cities("Little Rock, AR", "Fort Smith, AR", "Fayetteville, AR"),
    searchInLabel: searchIn(STATE_NAME.ARKANSAS),
    educationOrganizationName: doeName(
      "Arkansas Division of Elementary and Secondary Education",
    ),
    educationOrganizationAbbreviation: doeAbbr("DESE"),
    logo: arkansasLogoPng,
    mobileLogo: arkansasLogoPng,
    salaryAttribution: null,
  },
  IN: {
    title: title(STATE_NAME.INDIANA),
    defaultSearchAddress: address("Indiana"),
    topCities: cities("Indianapolis, IN", "Fort Wayne, IN", "Evansville, IN"),
    searchInLabel: searchIn(STATE_NAME.INDIANA),
    educationOrganizationName: doeName("Indiana Department of Education"),
    educationOrganizationAbbreviation: doeAbbr("IDOE"),
    logo: indianaLogoPng,
    mobileLogo: indianaLogoPng,
    salaryAttribution: null,
  },
  MO: {
    title: title(STATE_NAME.MISSOURI),
    defaultSearchAddress: address("Missouri"),
    topCities: cities("Kansas City, MO", "St. Louis, MO", "Springfield, MO"),
    searchInLabel: searchIn(STATE_NAME.MISSOURI),
    educationOrganizationName: doeName(
      "Missouri Department of Elementary and Secondary Education",
    ),
    educationOrganizationAbbreviation: doeAbbr("DESE"),
    logo: missouriLogoPng,
    mobileLogo: missouriLogoPng,
    salaryAttribution: salaryScheduleAttribution("MSTA", "https://msta.org/"),
  },
  OK: {
    title: title(STATE_NAME.OKLAHOMA),
    defaultSearchAddress: address("Oklahoma"),
    topCities: cities("Oklahoma City, OK", "Tulsa, OK", "Norman, OK"),
    searchInLabel: searchIn(STATE_NAME.OKLAHOMA),
    educationOrganizationName: doeName(
      "Oklahoma State Department of Education & Oklahoma Public School Resource Center",
    ),
    educationOrganizationAbbreviation: doeAbbr("OSDE & OPSRC"),
    logo: oklahomaLogoPng,
    mobileLogo: oklahomaMobileLogoPng,
    salaryAttribution: null,
  },
} as const satisfies Record<AdvertisedDiscoverBoard, BoardPresentation>;

const ALL_STATES_PRESENTATION = {
  title: title("All States"),
  defaultSearchAddress: address("Missouri"),
  topCities: cities("Indianapolis, IN", "Nashville, TN", "Kansas City, MO"),
  searchInLabel: null,
  educationOrganizationName: doeName("All States"),
  educationOrganizationAbbreviation: doeAbbr("ALL"),
  logo: null,
  mobileLogo: null,
  salaryAttribution: null,
} as const satisfies BoardPresentation;

export const ALL_STATES_ABBREVIATION = "ALL";

export type StateConfigAbbreviation =
  | DiscoverBoardState
  | typeof ALL_STATES_ABBREVIATION;

export type StateRoute = DiscoverBoardRoute;

export interface StateConfig extends BoardPresentation {
  readonly route: StateRoute;
  /** `"ALL"` on the all-states board. Narrow with `isIndividualStateConfig`. */
  readonly abbreviation: StateConfigAbbreviation;
  readonly supportsSavedSearches: boolean;
}

export type IndividualStateRoute = Exclude<
  StateRoute,
  typeof ALL_STATES_BOARD_ROUTE
>;
export type IndividualStateRouteUpperCase = Uppercase<IndividualStateRoute>;

export type IndividualStateConfig = StateConfig & {
  route: IndividualStateRoute;
  abbreviation: DiscoverBoardState;
};

const advertisedBoard = <K extends AdvertisedDiscoverBoard>(
  abbreviation: K,
): IndividualStateConfig => ({
  ...DISCOVER_BOARD_PRESENTATION[abbreviation],
  route: ADVERTISED_DISCOVER_BOARDS[abbreviation],
  abbreviation,
  supportsSavedSearches: true,
});

const ADVERTISED_BOARDS = {
  [ADVERTISED_DISCOVER_BOARDS.AR]: advertisedBoard("AR"),
  [ADVERTISED_DISCOVER_BOARDS.IN]: advertisedBoard("IN"),
  [ADVERTISED_DISCOVER_BOARDS.MO]: advertisedBoard("MO"),
  [ADVERTISED_DISCOVER_BOARDS.OK]: advertisedBoard("OK"),
} as const satisfies Record<AdvertisedBoardRoute, IndividualStateConfig>;

const DEPRECATED_BOARDS = {
  [DEPRECATED_DISCOVER_BOARDS.TN]: {
    route: DEPRECATED_DISCOVER_BOARDS.TN,
    abbreviation: "TN",
    title: title(STATE_NAME.TENNESSEE),
    defaultSearchAddress: address("Tennessee"),
    topCities: cities("Nashville, TN", "Memphis, TN", "Knoxville, TN"),
    searchInLabel: searchIn(STATE_NAME.TENNESSEE),
    educationOrganizationName: doeName("Tennessee Department of Education"),
    educationOrganizationAbbreviation: doeAbbr("TDOE"),
    logo: null,
    mobileLogo: null,
    salaryAttribution: null,
    supportsSavedSearches: false,
    contactEmail: email("TDOE.Communications@tn.gov"),
  },
} as const satisfies Record<DeprecatedBoardRoute, WalledStateConfig>;

const allStatesBoard: StateConfig = {
  ...ALL_STATES_PRESENTATION,
  route: ALL_STATES_BOARD_ROUTE,
  abbreviation: ALL_STATES_ABBREVIATION,
  supportsSavedSearches: true,
};

export const advertisedStates: readonly IndividualStateConfig[] =
  Object.values(ADVERTISED_BOARDS);

export const walledStates: readonly WalledStateConfig[] =
  Object.values(DEPRECATED_BOARDS);

const statesConfig = {
  ...ADVERTISED_BOARDS,
  ...DEPRECATED_BOARDS,
  [ALL_STATES_BOARD_ROUTE]: allStatesBoard,
} as const satisfies Record<StateRoute, StateConfig>;

export default statesConfig;

export const defaultState: StateConfig = statesConfig.all;

export const allStates: readonly StateConfig[] = Object.values(statesConfig);

const VALID_STATE_ROUTES = new Set<string>(allStates.map((s) => s.route));

export function isValidStateRoute(value: string): value is StateRoute {
  return VALID_STATE_ROUTES.has(value);
}

export function isIndividualStateRoute(
  route: StateRoute,
): route is IndividualStateRoute {
  return route !== ALL_STATES_BOARD_ROUTE;
}

export function isIndividualStateConfig(
  state: StateConfig,
): state is IndividualStateConfig {
  return state.route !== ALL_STATES_BOARD_ROUTE;
}

/** A board that still resolves but is no longer advertised. */
export type WalledStateConfig = IndividualStateConfig & {
  abbreviation: DeprecatedDiscoverBoard;
  contactEmail: Email;
};

export function isWalledStateConfig(
  state: StateConfig,
): state is WalledStateConfig {
  return (
    isIndividualStateConfig(state) &&
    isDeprecatedDiscoverBoard(state.abbreviation)
  );
}

export const individualStates: readonly IndividualStateConfig[] = [
  ...advertisedStates,
  ...walledStates,
];

const individualStateRoutesMap = new Map<string, IndividualStateRoute>(
  individualStates.map((s) => [s.route, s.route]),
);

export function getStateConfigFromString(route: string): StateConfig | null {
  if (isValidStateRoute(route)) {
    return statesConfig[route];
  }
  return null;
}

export function getIndividualStateRoute(
  value: string | undefined,
): IndividualStateRoute | undefined {
  if (!value) return undefined;
  return individualStateRoutesMap.get(value.toLowerCase());
}

const abbreviationToConfigMap = new Map<string, IndividualStateConfig>(
  individualStates.map((state) => [state.abbreviation, state]),
);

export function getStateConfigFromAbbreviation(
  abbreviation: string | undefined | null,
): IndividualStateConfig | null {
  if (!abbreviation) return null;
  return abbreviationToConfigMap.get(abbreviation) || null;
}

export function getAdvertisedStateConfigFromAbbreviation(
  abbreviation: StateAbbreviation | null,
): IndividualStateConfig | null {
  return (
    advertisedStates.find((state) => state.abbreviation === abbreviation) ??
    null
  );
}

export function getOrganizationPartnerName(state: StateConfig): string {
  if (state.route === statesConfig.oklahoma.route) {
    return state.educationOrganizationAbbreviation;
  }

  return state.educationOrganizationName;
}

export function getSalaryScheduleAttributionForState(
  stateAbbreviation: string | undefined,
): SalaryScheduleAttribution | null {
  return (
    getStateConfigFromAbbreviation(stateAbbreviation)?.salaryAttribution ?? null
  );
}
